import React, { useState, useEffect, useRef, useCallback } from "react";
import { useToast } from "../../Utils/toastContext";
import { useNavigate, useLocation } from "react-router-dom";
import { debounce } from "lodash";
import Dropdown from "../../Components/CustomDropdown/customdropdown";
import PermissionSection from "../../Components/Permissions/permission-section";

const RolesPermissionSettings = () => {
  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { showToast } = useToast();
  const [roleTypeData, setroleTypeData] = useState([]);
  const [roleTypeId, setRoleTypeId] = useState(1);
  const [roleType, setRoleType] = useState("Super Admin");
  const [loading, setLoading] = useState(false);
  const [originalPermissions, setOriginalPermissions] = useState({});

  const [preSelectedRoleType, setPreSelectedRoleType] = useState({});
  const [chunks, setChunks] = useState([]);
  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    if (Object.keys(permissions).length > 0) {
      const sectionEntries = Object.entries(permissions);
      const chunkSize = 3;
      const newChunks = [];

      for (let i = 0; i < sectionEntries.length; i += chunkSize) {
        newChunks.push(sectionEntries.slice(i, i + chunkSize));
      }
      setChunks(newChunks);
    }
  }, [permissions]);
  useEffect(() => {
    if (roleTypeData.length > 0) {
      const superAdminRole = roleTypeData.find((role) => role.value === 1);
      setPreSelectedRoleType(superAdminRole || roleTypeData[0]);
    }
  }, [roleTypeData]);
  // Toggle handler
  const handleToggle = (section, id) => {
    setPermissions((prev) => ({
      ...prev,
      [section]: prev[section].map((perm) =>
        perm.unique_id === id ? { ...perm, status: !perm.status } : perm
      ),
    }));
  };
  const buildPayload = () => {
    const payload = [];

    Object.keys(permissions).forEach((section) => {
      permissions[section].forEach((perm, idx) => {
        const originalStatus = originalPermissions[section]?.[idx]?.status;

        if (perm.status !== originalStatus) {
          payload.push({
            unique_id: perm.unique_id,
            status: perm.status,
          });
        }
      });
    });

    return payload;
  };
  const handleSelectRoleType = (selectedOption) => {
    console.log("Selected Option:", selectedOption.value);
    setRoleType(selectedOption.label);
    setRoleTypeId(selectedOption.value);
    setPreSelectedRoleType(selectedOption);
  };
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem("jwtToken");
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
          throw new Error("Session expired. Please log in again.");
        }
        const errorData = await response.json().catch(() => ({
          message: "An unexpected error occurred",
        }));
        throw new Error(errorData.message || "Something went wrong");
      }
      return await response.json();
    } catch (error) {
      console.error("API Call Error:", error); // Log full error for debugging
      throw new Error(
        typeof error.message === "string"
          ? error.message
          : "Network error, please try again later"
      );
    }
  };
  const getRoles = async () => {
    try {
      const response = await GetAPIcalls("/v1/role/all");

      if (response.status == 200) {
        const roles = [];

        response.data.forEach((profile) => {
          const formattedName = profile.role_name
            .toLowerCase() // make everything lowercase
            .replace(/_/g, " ") // replace underscores with spaces
            .replace(/\b\w/g, (char) => char.toUpperCase()); // capitalize each word
          roles.push({
            value: profile.id,
            label: formattedName,
          });
        });
        setroleTypeData(roles);
      } else {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
        }
      }
    } catch (err) {
      showToast(err.message, "error");
    } finally {
    }
  };
  const getRolePermissions = async () => {
    try {
      const response = await GetAPIcalls(
        `/v1/permissions/by-role/${roleTypeId}`
      );

      if (response.status == 200) {
        setPermissions(response.data.permissions);
        setOriginalPermissions(response.data.permissions); // deep copy if needed
      } else {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
        }
      }
    } catch (err) {
      showToast(err.message, "error");
    } finally {
    }
  };
  const updatePermission = async () => {
    const token = localStorage.getItem("jwtToken");
    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const apiUrl = `${BASE_URL}/v1/permissions/role/update`;

    const bodyData = {
      role_id: roleTypeId,
      permissions: buildPayload(),
    };
    try {
      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
          throw new Error("Session expired. Please log in again.");
        }
        const errorData = await response.json().catch(() => ({
          message: "An unexpected error occurred",
        }));
        throw new Error(errorData.message || "Something went wrong");
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Network error, please try again later");
    }
  };
  const handleUpdatePermissions = useCallback(
    debounce(async (e) => {
      e.preventDefault();
      let toastMessage = null;
      try {
        const response = await updatePermission();
        if (response.status == 200) {
          getRolePermissions();
          toastMessage = {
            message: "Details updated successfully.",
            type: "success",
          };
        } else {
          toastMessage = { message: response.message, type: "error" };
        }
      } catch (err) {
        toastMessage = { message: err.message, type: "error" };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000), // Debounce time
    [roleTypeId, permissions] // Dependencies
  );
  useEffect(() => {
    getRoles();
  }, []);

  useEffect(() => {
    getRolePermissions();
  }, [roleTypeId]);
  return (
    <div>
      <div className="lg:min-w-[78vw] lg:max-w-[80vw]">
        {loading && (
          <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
            <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        <div>
          <div className="bg-white mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-6">
              <div className="flex flex-col px-4 pt-4">
                <Dropdown
                  label="Select Role"
                  options={roleTypeData}
                  preselectedValue={preSelectedRoleType}
                  onSelect={handleSelectRoleType}
                />
              </div>
              {/* center column */}
              <div className="flex flex-col lg:pt-4 pt-0  border-l-0   border-gray-300 px-4"></div>
            </div>
            <div className="w-full p-4 bg-white rounded-lg shadow-lg">
              {/* Header */}
              <div className="flex w-full flex-col md:flex-row justify-between items-start md:items-center border-b pb-5 border-gray-200">
                <div className="flex items-center mb-2 md:mb-0">
                  {/* Shield Icon */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="black"
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                  >
                    <path d="M12 1l9 4v6c0 5-3.8 9.7-9 11-5.2-1.3-9-6-9-11V5l9-4zm-1 14.6l5.3-5.3-1.4-1.4L11 12.8l-1.9-1.9-1.4 1.4 3.3 3.3z" />
                  </svg>
                  <span className="font-semibold lg:text-xl text-base ml-2">
                    Permissions
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 w-full ">
                {chunks.map((chunk, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col md:pl-4 md:pt-2 md:border-l"
                  >
                    {chunk.map(([sectionName, sectionPerms]) => (
                      <PermissionSection
                        key={sectionName}
                        title={
                          sectionName.charAt(0).toUpperCase() +
                          sectionName.slice(1).replace("_", " ")
                        }
                        permissions={sectionPerms}
                        onToggle={handleToggle}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center mt-6 p-4 bg-[#005A9C]">
            <div className="flex items-center justify-start w-full space-x-4">
              <div
                onClick={(e) => {
                  e.preventDefault();
                  setLoading(true);
                  handleUpdatePermissions(e);
                }}
                className="flex border ml-6 rounded-full items-center space-x-2 text-sm md:text-md font-medium py-1 px-8 text-white cursor-pointer"
              >
                <span>Update settings</span>
              </div>
              <div
                onClick={(e) => {
                  e.preventDefault();
                  getRolePermissions();
                }}
                className="flex border ml-6 rounded-full items-center space-x-2 text-sm md:text-md font-medium py-1 px-8 text-white cursor-pointer"
              >
                <span>Reset</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolesPermissionSettings;
